// @safetyhub/report-composer — 등록형 위젯 전파(propagation) 순수 헬퍼
//
// 등록형 폼 위젯이 산출한 값(outputsValues)을 선언형 매핑(writes)에 따라 대상에 쓴다.
// 대상은 스칼라 bindingKey 또는 배열 일괄 `defKey[].field.path`(모든 행 깊은 set). 불변 갱신.
// 결정: 2026-07-10-composer-registered-form-widget-and-section §3.

import type { RegisteredWidgetWrite } from '@safetyhub/shared';

/** `defKey[].field.path` 배열 대상 파싱. 배열 대상이 아니면 null. */
function parseArrayTarget(target: string): { defKey: string; path: string } | null {
  const idx = target.indexOf('[].');
  if (idx < 0) return null;
  const defKey = target.slice(0, idx);
  const path = target.slice(idx + 3);
  if (!defKey || !path) return null;
  return { defKey, path };
}

/** 객체에 dot-path로 값을 깊은 set한 새 객체 반환(불변, 경로 중간 객체도 복사). */
function deepSet(
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const keys = path.split('.');
  const next = { ...obj };
  let cursor = next;
  for (let i = 0; i < keys.length - 1; i += 1) {
    const k = keys[i];
    const child = cursor[k];
    const copy: Record<string, unknown> =
      child && typeof child === 'object' && !Array.isArray(child)
        ? { ...(child as Record<string, unknown>) }
        : {};
    cursor[k] = copy;
    cursor = copy;
  }
  cursor[keys[keys.length - 1]] = value;
  return next;
}

/**
 * 등록형 위젯 전파 적용 — 각 write의 source 값을 대상에 쓴다(onChange 호출).
 * @param writes 위젯 정의의 전파 매핑(미지정이면 no-op).
 * @param outputsValues 위젯 산출 필드값(source 키 → 값).
 * @param values 전체 content(배열 대상 행 접근용).
 * @param onChange 값 반영 콜백(스칼라=target, 배열=defKey).
 *
 * - source 값이 undefined면 그 write는 skip(덮어쓰기 안 함).
 * - 배열 대상(`defKey[].path`)이면 values[defKey](행 배열)의 모든 행에 path 깊은 set(불변). 배열 아니면 no-op.
 * - 스칼라 대상이면 onChange(target, value).
 */
/**
 * 전파 기록 중 회수(recall) 가능한 스칼라 기록만 수집 — applyRegisteredWrites와 동일한 skip 규칙
 * (source 값 undefined면 skip)을 따르되, 배열 일괄 target(`defKey[].path`)은 제외한다.
 * 배열 행에는 전파 외 다른 데이터가 함께 있어 행 전체를 옛값으로 되돌리기 위험(회수 대상 아님).
 */
export function collectScalarRegisteredWrites(
  writes: RegisteredWidgetWrite[] | undefined,
  outputsValues: Record<string, unknown>,
): { target: string; value: unknown }[] {
  if (!writes || writes.length === 0) return [];
  const out: { target: string; value: unknown }[] = [];
  for (const write of writes) {
    const value = outputsValues[write.source];
    if (value === undefined) continue;
    if (parseArrayTarget(write.target)) continue;
    out.push({ target: write.target, value });
  }
  return out;
}

export function applyRegisteredWrites(
  writes: RegisteredWidgetWrite[] | undefined,
  outputsValues: Record<string, unknown>,
  values: Record<string, unknown>,
  onChange: (bindingKey: string, value: unknown) => void,
): void {
  if (!writes || writes.length === 0) return;
  for (const write of writes) {
    const value = outputsValues[write.source];
    if (value === undefined) continue;
    const arr = parseArrayTarget(write.target);
    if (arr) {
      const rows = values[arr.defKey];
      if (!Array.isArray(rows)) continue; // 대상이 배열이 아니면 무시(no-op)
      const next = rows.map((row) =>
        deepSet(
          row && typeof row === 'object' ? (row as Record<string, unknown>) : {},
          arr.path,
          value,
        ),
      );
      onChange(arr.defKey, next);
    } else {
      onChange(write.target, value);
    }
  }
}
